"use client";

import { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Constellation from "./Constellation";
import SynapseLines from "./SynapseLines";
import GhostStream from "./GhostStream";
import type { GhostStreamItem } from "@/lib/atlas/useAtlasGhostStream";

const LONG_REQUEST_MS = 9000;

export interface AtlasProcessingOverlayProps {
  pending: boolean;
  /** When set, overlay positions with getBoundingClientRect (for reuse in floating card etc.). Otherwise fills parent (inset-0). */
  anchorRef?: React.RefObject<HTMLElement | null>;
  ghostStreamItems: GhostStreamItem[];
  primaryColor: string;
  secondaryColor: string;
  onCollapseDone?: () => void;
  /** Heuristic: more nodes for longer prompts (6–18). */
  messageLength?: number;
}

type Phase = "ignition" | "birth" | "sync" | "collapse";

const NODE_COUNT = (len: number) => Math.min(10, Math.max(5, Math.floor(len / 30) + 5));

/**
 * Atlas Constellation Synthesis – processing theatre anchored to the assistant pending bubble.
 * Collapse runs when stream finishes (full reply). Optional anchorRef for positioning in any layout.
 */
export default function AtlasProcessingOverlay({
  pending,
  anchorRef,
  ghostStreamItems,
  primaryColor,
  secondaryColor,
  onCollapseDone,
  messageLength = 0,
}: AtlasProcessingOverlayProps) {
  const [phase, setPhase] = useState<Phase>("ignition");
  const [collapseProgress, setCollapseProgress] = useState(0);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const [longRequest, setLongRequest] = useState(false);
  const reducedMotion = useRef(false);
  const collapseDone = useRef(false);

  const nodeCount = NODE_COUNT(messageLength);

  useEffect(() => {
    reducedMotion.current = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  useEffect(() => {
    if (!pending) return;
    setPhase("ignition");
    setCollapseProgress(0);
    setLongRequest(false);
    collapseDone.current = false;
  }, [pending]);

  useEffect(() => {
    if (!pending) return;
    const t1 = setTimeout(() => setPhase("birth"), 250);
    const t2 = setTimeout(() => setPhase("sync"), 700);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [pending]);

  useEffect(() => {
    if (!pending) return;
    const id = setTimeout(() => setLongRequest(true), LONG_REQUEST_MS);
    return () => clearTimeout(id);
  }, [pending]);

  useEffect(() => {
    if (pending) return;
    setPhase("collapse");
    const start = performance.now();
    const duration = 220;
    const tick = () => {
      const elapsed = performance.now() - start;
      const p = Math.min(1, elapsed / duration);
      setCollapseProgress(p);
      if (p < 1) requestAnimationFrame(tick);
      else {
        if (!collapseDone.current && onCollapseDone) {
          collapseDone.current = true;
          onCollapseDone();
        }
      }
    };
    const id = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(id);
  }, [pending, onCollapseDone]);

  useEffect(() => {
    if (!anchorRef?.current) return;
    const el = anchorRef.current;
    const update = () => setAnchorRect(el.getBoundingClientRect());
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    const scrollParent = el.closest(".overflow-y-auto") ?? document.documentElement;
    scrollParent.addEventListener("scroll", update, { passive: true });
    return () => {
      ro.disconnect();
      scrollParent.removeEventListener("scroll", update);
    };
  }, [anchorRef, pending]);

  if (!pending && collapseProgress >= 1) return null;

  const reduced = reducedMotion.current;
  const useAnchor = anchorRef && anchorRect;

  return (
    <AnimatePresence mode="wait">
      <motion.div
        className={`z-20 pointer-events-none rounded-2xl overflow-hidden ${!reduced ? "atlas-pill-breathe" : ""}`}
        style={{
          ...(!reduced && { animation: "atlas-pill-breathe 3.5s ease-in-out infinite" }),
          ...(useAnchor
          ? {
              position: "fixed",
              left: anchorRect?.left ?? 0,
              top: anchorRect?.top ?? 0,
              width: anchorRect?.width ? Math.max(anchorRect.width, 200) : 200,
              height: anchorRect?.height ? Math.max(anchorRect.height, 80) : 80,
            }
          : { position: "absolute", inset: 0 }),
        }}
        initial={false}
        animate={{
          opacity: pending ? 1 : collapseProgress >= 1 ? 0 : 1,
        }}
        transition={{ duration: 0.2 }}
      >
        {/* Base: soft tint so the pill is one cohesive shape, not a hard white frame */}
        <div
          className="absolute inset-0 rounded-2xl"
          style={{
            background: `linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.88) 100%)`,
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
          }}
        />
        {/* Inner glow: single gradient so it feels alive and free-flowing */}
        <div
          className="absolute inset-0 rounded-2xl"
          style={{
            background: `radial-gradient(ellipse 85% 70% at 50% 45%, ${primaryColor}22 0%, ${secondaryColor}10 45%, transparent 72%)`,
            opacity: phase !== "ignition" ? 1 : 0.75,
            animation: longRequest && pending ? "atlas-ambient-pulse 2.5s ease-in-out infinite" : undefined,
            pointerEvents: "none",
          }}
        />
        {/* B) Ghost stream layer (masked, blurred) */}
        <GhostStream
          items={ghostStreamItems}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
        />
        {/* A) Constellation + lines (foreground) */}
        <div className="absolute inset-0 rounded-2xl overflow-hidden">
          <Constellation
            nodeCount={nodeCount}
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
            phase={reduced ? "sync" : phase}
            collapseProgress={collapseProgress}
            reducedMotion={reduced}
          />
          <SynapseLines
            nodeCount={nodeCount}
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
            phase={phase}
            collapseProgress={collapseProgress}
            reducedMotion={reduced}
          />
        </div>
        {/* Soft glow border – subtle so the pill feels cohesive */}
        <motion.div
          className="absolute inset-0 rounded-2xl pointer-events-none"
          style={{
            boxShadow: `inset 0 0 0 1px ${primaryColor}20, 0 0 16px ${primaryColor}12`,
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: phase === "ignition" ? 0.5 : 0.25 }}
          transition={{ duration: 0.25 }}
        />
      </motion.div>
    </AnimatePresence>
  );
}
